import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { getWindowLabel, getLocalDateString, TimeWindow } from "@/lib/time-utils";
import { checkCurrentUserSubscription } from "@/lib/subscription-utils";
import { Calendar, Clock, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { gu } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { AppFooter } from "@/components/AppFooter";

interface Session {
  id: string;
  title_gu: string;
  title_hi: string | null;
  description_gu: string;
  description_hi: string | null;
  yt_video_id: string;
  video_language: 'gu' | 'hi';
  date: string;
  windows: TimeWindow[];
  notes: string;
  thumbnail_url: string | null;
  thumbnail_active: boolean;
}

export default function Upcoming() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logo, strings, language } = useLanguage();

  const getSessionContent = (session: Session) => {
    if (language === 'hi') {
      return {
        title: session.title_hi || session.title_gu,
        description: session.description_hi || session.description_gu,
      };
    }
    return {
      title: session.title_gu,
      description: session.description_gu,
    };
  };

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        fetchUpcomingSessions();
        checkSubscription();
      }
    });

    return () => subscription.unsubscribe();
  }, [language]);

  const checkSubscription = async () => {
    setCheckingSubscription(true);
    const hasSubscription = await checkCurrentUserSubscription();
    setHasActiveSubscription(hasSubscription);
    setCheckingSubscription(false);
  };

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('sessions-upcoming')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
          filter: 'status=eq.published'
        },
        (payload) => {
          console.log('Session change detected:', payload);
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            toast({
              title: strings.newLessonAdded,
              description: strings.newUpcomingLesson,
            });
          }
          fetchUpcomingSessions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
    }
  };

  const fetchUpcomingSessions = async () => {
    try {
      const today = getLocalDateString();
      
      const { data, error } = await supabase
        .from("sessions")
        .select("*")
        .gt("date", today)
        .eq("status", "published")
        .eq("video_language", language)
        .order("date", { ascending: true })
        .limit(10);

      if (error) throw error;

    const sessionData = (data || []).map((session) => ({
      ...session,
      video_language: (session.video_language as 'gu' | 'hi'),
      windows: (session.windows as unknown as TimeWindow[]) || [],
    }));

      setSessions(sessionData);
    } catch (error: any) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    };
    return date.toLocaleDateString('gu-IN', options);
  };

  if (authLoading || loading || checkingSubscription) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="દમયંતી બાગ" className="h-10" />
            <h1 className="text-xl font-bold text-primary">{strings.upcoming}</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-4">
        {sessions.length > 0 ? (
          sessions.map((session) => {
            const content = getSessionContent(session);
            return (
              <MbCard key={session.id} elevated className="overflow-hidden">
                {session.thumbnail_active && session.thumbnail_url ? (
                  <div className="relative aspect-video">
                    <img
                      src={session.thumbnail_url}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="text-xs font-semibold mb-1">🎬 COMING SOON</p>
                        <p className="text-sm">{formatDate(session.date)}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="flex gap-4 p-4">
                  {!session.thumbnail_active && (
                    <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-lg flex flex-col items-center justify-center relative">
                      <Calendar className="w-6 h-6 text-primary mb-1" />
                      <span className="text-xs font-bold text-primary">
                        {new Date(session.date).getDate()}
                      </span>
                      {!hasActiveSubscription && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                          <Lock className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-foreground mb-1 line-clamp-2">
                      {content.title}
                    </h3>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {formatDate(session.date)}
                    </p>

                    {content.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {content.description}
                      </p>
                    )}

                    {/* Time Windows */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {session.windows.map((window, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border"
                        >
                          <Clock className="w-3 h-3 inline mr-1" />
                          {getWindowLabel(window.label)}: {window.from} - {window.to}
                        </div>
                      ))}
                    </div>

                    {/* Subscription Required Message */}
                    {!hasActiveSubscription && (
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-foreground flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          {strings.videoAvailableOn.replace('%DATE%', formatDate(session.date))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {strings.subscribeForAccess}
                        </p>
                        <Button
                          size="sm"
                          variant="default"
                          className="w-full text-xs h-8"
                          onClick={() => navigate("/subscribe")}
                        >
                          {strings.subscribeToWatch}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </MbCard>
            );
          })
        ) : (
          <MbCard className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{strings.noUpcomingLessons}</h2>
            <p className="text-muted-foreground">{strings.newLessonsWillAppear}</p>
          </MbCard>
        )}
      </div>

      <div className="pb-16">
        <AppFooter />
      </div>

      <BottomNav />
    </div>
  );
}
